import {
  ArrowsClockwiseIcon,
  CaretDownIcon,
  GearIcon,
  LightningIcon,
  PlusIcon,
  TreeViewIcon,
} from '@phosphor-icons/react'
import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu'
import type { Tool } from '../../utils/api-client/api-schemas'
import { getToolName } from '../../utils/repository-utils.tsx'
import * as buttonStyles from '../buttons/default-button.css.ts'
import type { RepositoryActionOverlaysUIState } from '../repository-action-overlays'
import { ToolIcon } from '../third-party-branding/tool-icon.tsx'
import * as styles from './repository-action-menu.css'

type RepositoryActionMenuProps = Pick<RepositoryActionOverlaysUIState, 'setCurrentOverlay'> & {
  mappedTools?: Tool[]
  onSyncTool?: (tool: Tool) => unknown
  /** Whether to offer the admin-only write controls — see `useAdminRole`. */
  hasWriteAccess: boolean
}

export const RepositoryActionMenu: React.FC<RepositoryActionMenuProps> = ({
  setCurrentOverlay,
  mappedTools = [],
  onSyncTool,
  hasWriteAccess,
}) => {
  return (
    <RadixDropdownMenu.Root>
      {/* The recipe rather than DefaultButton itself: DefaultButton doesn't pass through the props and
          handlers Radix's `asChild` needs to drive the menu. */}
      <RadixDropdownMenu.Trigger className={buttonStyles.button({ buttonType: 'tertiary', size: 'small' })}>
        Repository actions
        <CaretDownIcon className={styles.expander} size={16} weight="fill" />
      </RadixDropdownMenu.Trigger>

      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content onCloseAutoFocus={e => e.preventDefault()} className={styles.content}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <RepositoryActionMenuItem
              icon={<PlusIcon size={15} />}
              label="Add findings for analysis"
              action={() => setCurrentOverlay('add-findings')}
            />
            {mappedTools.length > 0 && onSyncTool && (
              <RadixDropdownMenu.Sub>
                <RadixDropdownMenu.SubTrigger className={styles.menuItem}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexDirection: 'row' }}>
                    <ArrowsClockwiseIcon size={15} />
                    <span className={styles.menuItemLabel}>Sync tool findings</span>
                  </div>
                </RadixDropdownMenu.SubTrigger>
                <RadixDropdownMenu.Portal>
                  <RadixDropdownMenu.SubContent className={styles.subContent} sideOffset={8}>
                    {mappedTools.map(tool => (
                      <RadixDropdownMenu.DropdownMenuItem
                        key={tool}
                        className={styles.menuItem}
                        onSelect={() => onSyncTool(tool)}
                      >
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexDirection: 'row' }}>
                          <ToolIcon tool={tool} size="small" />
                          <span className={styles.menuItemLabel}>{getToolName(tool)}</span>
                        </div>
                      </RadixDropdownMenu.DropdownMenuItem>
                    ))}
                  </RadixDropdownMenu.SubContent>
                </RadixDropdownMenu.Portal>
              </RadixDropdownMenu.Sub>
            )}
            {/* Creating a workflow is admin-only on the platform (ISS-8480). Manage workflows
                below is the read view and stays for everyone. No explanation is rendered in its
                place: a dropdown is a list of what you can do, so a missing item reads correctly,
                whereas a disabled-looking row would invite clicking. */}
            {hasWriteAccess && (
              <RepositoryActionMenuItem
                icon={<LightningIcon size={15} />}
                label="Create workflow"
                action={() => setCurrentOverlay('new-workflow')}
              />
            )}
            <RepositoryActionMenuItem
              icon={<TreeViewIcon size={15} />}
              label="Manage workflows"
              action={() => setCurrentOverlay('manage-workflows')}
            />
            <RepositoryActionMenuItem
              icon={<GearIcon size={15} />}
              label="Repository settings"
              action={() => setCurrentOverlay('repository-settings')}
            />
          </div>
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  )
}

type RepositoryActionMenuItemProps = {
  icon: React.ReactNode
  label: string
  action: () => unknown
}

const RepositoryActionMenuItem: React.FC<RepositoryActionMenuItemProps> = ({ icon, label, action }) => {
  return (
    <RadixDropdownMenu.DropdownMenuItem className={styles.menuItem} onSelect={action}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexDirection: 'row' }}>
        {icon}
        <span className={styles.menuItemLabel}>{label}</span>
      </div>
    </RadixDropdownMenu.DropdownMenuItem>
  )
}
